import React, { useEffect, useMemo, useRef, useState } from 'react';
import OrderDesign from '../OrderDesign';
import { REPOSITION_MOVE_DURATION_MS } from './repositionAnimationTiming';

function findViewerSlotRect(zone, index) {
  if (!zone || index == null) {
    return null;
  }
  const slotSelector = `[data-slot-zone="${zone}"][data-slot-index="${index}"]`;
  const slotElement = document.querySelector(slotSelector);
  if (slotElement) {
    const rect = slotElement.getBoundingClientRect();
    return {
      left: rect.left + window.scrollX,
      top: rect.top + window.scrollY,
      width: rect.width || 180,
      height: rect.height || 120,
    };
  }

  const isYourZone = zone.startsWith('your_');
  const zoneSuffix = zone.replace(/^(your|opponent)_/, '');
  const zoneSelector = isYourZone
    ? `.battle-zone.${zoneSuffix}`
    : `.battle-zone.opponent-${zoneSuffix}`;
  const zoneContainer = document.querySelector(zoneSelector);
  if (zoneContainer) {
    const slots = Array.from(zoneContainer.querySelectorAll('[data-slot-zone][data-slot-index]'));
    const targetSlot = slots.find((slot) => {
      const slotIndex = parseInt(slot.getAttribute('data-slot-index'), 10);
      const slotZone = slot.getAttribute('data-slot-zone');
      return slotIndex === index && slotZone === zone;
    });
    if (targetSlot) {
      const rect = targetSlot.getBoundingClientRect();
      return {
        left: rect.left + window.scrollX,
        top: rect.top + window.scrollY,
        width: rect.width || 180,
        height: rect.height || 120,
      };
    }
  }

  return null;
}

function isValidRect(rect) {
  return (
    rect
    && Number.isFinite(rect.left)
    && Number.isFinite(rect.top)
    && Number.isFinite(rect.width)
    && Number.isFinite(rect.height)
    && rect.width > 0
    && rect.height > 0
  );
}

function buildLayout(fromZone, fromIndex, toZone, toIndex) {
  const start = findViewerSlotRect(fromZone, fromIndex);
  const end = findViewerSlotRect(toZone, toIndex);
  if (!isValidRect(start) || !isValidRect(end)) {
    return null;
  }

  const startCenterX = start.left + start.width / 2;
  const startCenterY = start.top + start.height / 2;
  const endCenterX = end.left + end.width / 2;
  const endCenterY = end.top + end.height / 2;
  const startLeft = startCenterX - start.width / 2;
  const startTop = startCenterY - start.height / 2;

  return {
    startLeft,
    startTop,
    width: start.width,
    height: start.height,
    dx: endCenterX - startCenterX,
    dy: endCenterY - startCenterY,
  };
}

/**
 * Flies a stolen unit from the enemy slot to the stealer's slot (Phase Traverse style).
 */
export const HarralMindLeechUnitStealFlyOverlay = ({
  grant,
  unit,
  gameState = null,
  userId = null,
  onComplete,
}) => {
  const [layout, setLayout] = useState(null);
  const [atEnd, setAtEnd] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const measureGenerationRef = useRef(0);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const grantKey = grant?.key ?? '';
  const fromZone = grant?.fromZone ?? '';
  const fromIndex = grant?.fromIndex;
  const toZone = grant?.toZone ?? '';
  const toIndex = grant?.toIndex;

  useEffect(() => {
    if (!grant || !unit || fromZone == null || fromIndex == null || toZone == null || toIndex == null) {
      return undefined;
    }

    const generation = measureGenerationRef.current + 1;
    measureGenerationRef.current = generation;
    setLayout(null);
    setAtEnd(false);
    setAnimationComplete(false);

    let raf2 = null;
    let retryTimer = null;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (measureGenerationRef.current !== generation) {
          return;
        }
        const measured = buildLayout(fromZone, fromIndex, toZone, toIndex);
        if (measured) {
          setLayout(measured);
          return;
        }
        retryTimer = setTimeout(() => {
          if (measureGenerationRef.current !== generation) {
            return;
          }
          const retryMeasured = buildLayout(fromZone, fromIndex, toZone, toIndex);
          if (retryMeasured) {
            setLayout(retryMeasured);
          }
        }, 100);
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) {
        cancelAnimationFrame(raf2);
      }
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, [grantKey, fromZone, fromIndex, toZone, toIndex, grant, unit]);

  useEffect(() => {
    if (!layout || animationComplete) {
      return undefined;
    }

    let raf2 = null;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setAtEnd(true);
      });
    });

    const fallbackTimer = setTimeout(() => {
      setAnimationComplete(true);
      if (onCompleteRef.current) {
        onCompleteRef.current();
      }
    }, REPOSITION_MOVE_DURATION_MS + 80);

    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) {
        cancelAnimationFrame(raf2);
      }
      clearTimeout(fallbackTimer);
    };
  }, [layout, animationComplete]);

  const zoneName = useMemo(() => toZone, [toZone]);

  if (!grant || !unit || !layout || animationComplete) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        left: `${layout.startLeft}px`,
        top: `${layout.startTop}px`,
        width: `${layout.width}px`,
        height: `${layout.height}px`,
        zIndex: 100000,
        pointerEvents: 'none',
        transform: atEnd ? `translate(${layout.dx}px, ${layout.dy}px)` : 'translate(0, 0)',
        transition: atEnd
          ? `transform ${REPOSITION_MOVE_DURATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`
          : 'none',
        willChange: 'transform',
        isolation: 'isolate',
      }}
      onTransitionEnd={(event) => {
        if (event.propertyName !== 'transform') {
          return;
        }
        setAnimationComplete(true);
        if (onCompleteRef.current) {
          onCompleteRef.current();
        }
      }}
    >
      <OrderDesign
        order={unit}
        minimal
        zoneName={zoneName}
        slotIndex={toIndex}
        gameState={gameState}
        userId={userId}
      />
    </div>
  );
};

export default HarralMindLeechUnitStealFlyOverlay;
